import React, { useEffect, useState } from "react";
import AppLayout from "../components/AppLayout";
import { useLocation } from "react-router-dom";
import { captureException } from "../monitoring/sentry";
import { HiExclamationTriangle } from "react-icons/hi2";
import { MdRefresh, MdHome } from "react-icons/md";
import "./ErrorBoundary.css";

function ErrorBoundary({ children }) {
  const [hasError, setHasError] = useState(false);
  const location = useLocation();
  useEffect(() => {
    if (hasError) {
      setHasError(false);
    }
  }, [location.key]);
  return (
    <ErrorBoundaryInner hasError={hasError} setHasError={setHasError}>
      {children}
    </ErrorBoundaryInner>
  );
}

const isCacheRelatedError = (error) => {
  if (!error) return false;
  const message = error.message || error.toString();
  const cacheRelatedPatterns = [
    /failed to fetch/i,
    /json.parse/i,
    /module not found/i,
    /chunk/i,
    /failed to load/i,
    /cannot find module/i,
    /syntax error/i,
  ];
  return cacheRelatedPatterns.some((pattern) => pattern.test(message));
};

class ErrorBoundaryInner extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, isCacheError: false };
  }

  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      error: error,
      isCacheError: isCacheRelatedError(error),
    };
  }

  componentDidUpdate(prevProps, _previousState) {
    if (!this.props.hasError && prevProps.hasError) {
      this.setState({ hasError: false, error: null });
    }
  }

  componentDidCatch(error, _errorInfo) {
    captureException(error);
    this.props.setHasError(true);
  }

  handleRefresh = () => {
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.getRegistrations().then((registrations) => {
        for (let registration of registrations) {
          registration.unregister();
        }
      });
    }
    window.location.href = window.location.href;
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    const isCacheError = this.state.isCacheError;

    return (
      <AppLayout>
        <div className="error-boundary-container">
          <div
            className={`error-card ${isCacheError ? "error-card-cache" : "error-card-default"}`}
          >
            <div className="error-icon-wrapper">
              <HiExclamationTriangle className="error-icon" />
            </div>

            <h2 className="error-title">
              {isCacheError
                ? "Tu navegador necesita actualizarse"
                : "Algo salió mal"}
            </h2>

            <p className="error-description">
              {isCacheError
                ? "Detectamos que tu navegador tiene una versión antigua de nuestra plataforma. Esto ocurre raramente después de nuestras actualizaciones. ¡No te preocupes, es muy fácil de resolver!"
                : "Encontramos un error inesperado. Por favor intenta recargar la página. Si el problema persiste, contacta a nuestro equipo de soporte."}
            </p>

            {isCacheError && (
              <div className="error-steps">
                <div className="step">
                  <span className="step-number">1</span>
                  <p>Haz clic en el botón "Actualizar ahora".</p>
                </div>
                <div className="step">
                  <span className="step-number">2</span>
                  <p>Espera a que la página se recargue completamente.</p>
                </div>
                <div className="step">
                  <span className="step-number">3</span>
                  <p>¡Listo! Ya estarás con la versión más reciente.</p>
                </div>
              </div>
            )}

            <div className="error-actions">
              <button className="btn-primary-error" onClick={this.handleRefresh}>
                <MdRefresh className="btn-icon" />
                {isCacheError ? "Actualizar ahora" : "Recargar página"}
              </button>
              <a href="/home" className="btn-secondary-error">
                <MdHome className="btn-icon" />
                Ir a inicio
              </a>
            </div>

            {!isCacheError && (
              <div className="error-footer">
                <p>
                  Contacta a <strong>soporte@trinitalkbot.com</strong> si necesitas ayuda.
                </p>
              </div>
            )}
          </div>
        </div>
      </AppLayout>
    );
  }
}

export default ErrorBoundary;
